import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled, { css } from 'styled-components';
import { toast } from 'react-hot-toast';
import unknownImage from '../../../images/unknown.png';
import { CSVLink } from "react-csv";
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCaretRight,
  faCaretDown,
  faFileCsv,
} from '@fortawesome/free-solid-svg-icons';
import UserCard from '../../../components/UserCard';
import Api from '../../../utils/Api';

const fetchMyGifts = async id => {
  if (!id) return null;
  const [error, group] = await Api.getMyGroupGifts(id);
  if (!error) return group;
  else {
    toast.error(Api.error(error));
    return null;
  }
}

const myGiftsStyles = css`
display: flex;
flex-direction: column;
gap: 20px;
width: 100%;
> .download {
  display: flex;
  gap: 5px;
  & a { 
    color: white;
    display: flex;
    gap: 10px;
    align-items: center;
  }
}

> .recipient {
  display: flex;
  flex-direction:column;
  gap: 5px;
  width: 100%;

  > .user {
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    padding-right: 10px;
    column-gap: 5px;

    > .user-card {
      border: none;
      max-width: 300px;
    }
  }
  > .gifts {
    border-top: 1px solid #cccccc;
    border-bottom: 1px solid #cccccc;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-left: 20px;
    padding-bottom: 5px;
    padding-top: 5px;

    > .gift {
      display: grid;
      grid-template-columns: 50px auto;
      column-gap: 10px;
      align-items: center;
      overflow: hidden;
      text-overflow: ellipsis;
      color: blue;

      &:hover {
        cursor: pointer;
      }

      > .image {
        > img {
          width: 50px;
          aspect-ratio: 1/1;
        }
      }
    }
  }
}`;

const MyGifts = styled(({ className, group }) => {
  const { data: myGifts = [] } = useQuery(['my-gifts', group._id], () => fetchMyGifts(group._id));
  const navigate = useNavigate();

  const byPerson = myGifts?.reduce((res, gift) => {
    const recipient = gift.list.user;
    res[recipient._id] = res[recipient._id] || {
      recipient,
      gifts: [],
    };
    res[recipient._id].gifts.push(gift);
    return (res);
  }, {}) ?? {};

  const inGroup = [];
  const csvData = [];
  const csvHeaders = [
    { label: 'First Name', key: 'firstname' },
    { label: 'Last Name', key: 'lastname' },
    { label: 'Email', key: 'email' },
    { label: 'Description', key: 'description' },
    { label: 'Price', key: 'price' },
    { label: 'Reserved On', key: 'reservedOn' },
    { label: 'Product URL', key: 'url' },
    { label: 'Image URL', key: 'image' },
  ]

  group.members.forEach((member => {
    const entry = byPerson[member._id] ?? {
      recipient: member,
      gifts: [],
    }
    inGroup.push(entry);

    entry.gifts.forEach(gift => {
      csvData.push({
        firstname: entry.recipient.firstname,
        lastname: entry.recipient.lastname,
        email: entry.recipient.email,
        description: gift.description,
        price: gift.price,
        reservedOn: moment(gift.reservedOn).format('YYYY-MM-DD HH:mm:ss'),
        url: gift.url,
        image: gift.image,
      })
    })
  }));

  const GiftCard = ({ gift }) => (
    <div className='gift' onClick={() => navigate(`/lists/details/${gift.list._id}?context=group&id=${group._id}`)} >
      <div className='image'>
        <img src={gift.image ? gift.image : unknownImage} alt='gift' />
      </div>
      <div className='description'>
        {gift.description.length > 100 ? `${gift.description.substring(0, 100)}...` : gift.description}
      </div>
    </div>
  )

  const MemberGifts = ({ data }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <div className='recipient'>
        <div className='user' onClick={() => setIsOpen(!isOpen)}>
          <UserCard className='user-card' user={data.recipient} />
          <span>{`${data.gifts?.length ?? 0}`}</span>
          <span>
            {
              data.gifts?.length > 0
                ? isOpen
                  ? <FontAwesomeIcon icon={faCaretDown} />
                  : <FontAwesomeIcon icon={faCaretRight} />
                : ' '
            }
          </span>
        </div>
        {
          data.gifts?.length > 0 && isOpen &&
          <div className='gifts'>
            {data.gifts?.map((gift, i) => (<GiftCard key={i} gift={gift} />))}
          </div>
        }
      </div >)
  }

  const GroupGifts = ({ gifts }) => Object.entries(gifts).map(([id, data]) => <MemberGifts key={id} data={data} />);

  return (
    <div className={className}>
      <div className='download'>
        <button className='primary'>
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={"my-gifts.csv"}
            className="primary"
            target="_blank"
            title='Download CSV File'
          >
            <span>Download</span>
            <FontAwesomeIcon icon={faFileCsv} />
          </CSVLink>
        </button>
      </div>
      <GroupGifts gifts={inGroup} />
    </div>
  )
})`${myGiftsStyles}`

export default MyGifts;